import React from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  success?: string;
  helperText?: string;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      success,
      helperText,
      icon,
      fullWidth = false,
      className = '',
      id,
      type = 'text',
      disabled = false,
      required = false,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substring(2, 11)}`;
    const hasError = !!error;
    const hasSuccess = !!success && !hasError;

    const baseClasses =
      'px-4 py-2 text-base rounded-lg border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed disabled:text-gray-500 dark:disabled:text-gray-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white';

    const stateClasses = hasError
      ? 'border-red-500 focus:border-red-500 focus:ring-red-200 dark:focus:ring-red-800 text-red-900 dark:text-red-400'
      : hasSuccess
        ? 'border-green-500 focus:border-green-500 focus:ring-green-200 dark:focus:ring-green-800'
        : 'border-gray-300 dark:border-gray-600 focus:border-primary focus:ring-primary/20';

    const widthClasses = fullWidth ? 'w-full' : '';
    const iconPaddingClasses = icon ? 'pl-12' : '';

    const inputClasses = `${baseClasses} ${stateClasses} ${widthClasses} ${iconPaddingClasses} ${className}`;

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <label
            htmlFor={inputId}
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {label}
            {required && <span className="ml-1 text-red-500" aria-label="Champ requis">*</span>}
          </label>
        )}

        <div className="relative">
          {icon && (
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              {icon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            type={type}
            className={inputClasses}
            disabled={disabled}
            required={required}
            aria-invalid={hasError}
            aria-describedby={
              error
                ? `${inputId}-error`
                : success
                  ? `${inputId}-success`
                  : helperText
                    ? `${inputId}-helper`
                    : undefined
            }
            {...props}
          />

          {(hasError || hasSuccess) && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              {hasError && (
                <AlertCircle
                  className="text-red-500"
                  size={20}
                  aria-label="Erreur"
                />
              )}
              {hasSuccess && (
                <CheckCircle2
                  className="text-green-500"
                  size={20}
                  aria-label="Valide"
                />
              )}
            </div>
          )}
        </div>

        {error && (
          <p
            id={`${inputId}-error`}
            className="mt-1 flex items-center gap-1 text-sm text-red-600"
            role="alert"
          >
            <AlertCircle size={14} />
            {error}
          </p>
        )}

        {success && !error && (
          <p
            id={`${inputId}-success`}
            className="mt-1 flex items-center gap-1 text-sm text-green-600"
          >
            <CheckCircle2 size={14} />
            {success}
          </p>
        )}

        {helperText && !error && !success && (
          <p
            id={`${inputId}-helper`}
            className="mt-1 text-sm text-gray-500 dark:text-gray-400"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
